import {useRef, useState} from "react";
import className from "classnames/bind";
import {ExportButtons} from "~/components";
import ScoreOverview from "./ScoreOverview";
import CrawlSummary from "./CrawlSummary";
import CriteriaGroup from "./CriteriaGroup";
import SerpPreview from "./SerpPreview";
import WebsitePreview from "./WebsitePreview";
import style from "../style/SeoCheck.module.scss";

const cn = className.bind(style);

const STATUS_LABEL = {pass: 'Đạt', warn: 'Cần cải thiện', fail: 'Lỗi'};

/** Host của URL (đặt tên file gọn). */
function hostOf(url) {
	try { return new URL(url).hostname; } catch { return 'seo'; }
}

/** Chuyển kết quả chấm điểm → dữ liệu export (Excel sheets + PDF sections). */
function buildSeoExport(result) {
	const meta = result.meta || {};
	const url  = meta.url || '';

	const overviewRows = [
		['URL', url],
		['Điểm tổng', `${result.score ?? 0}/100`],
		['Xếp hạng', `${result.grade ?? ''}${result.grade_label ? ' — ' + result.grade_label : ''}`],
	];

	const groupRows = (result.groups || []).map((g) => [g.label, `${g.score}/${g.max}`]);

	const criteriaHead = ['Nhóm', 'Tiêu chí', 'Trạng thái', 'Điểm', 'Nhận xét', 'Gợi ý'];
	const criteriaRows = [];

	(result.groups || []).forEach((g) => {
		(g.items || []).forEach((it) => {
			criteriaRows.push([
				g.label,
				it.label,
				STATUS_LABEL[it.status] || it.status || '',
				`${it.score}/${it.max}`,
				it.message || '',
				it.recommendation || '',
			]);
		});
	});

	const filename = `bao-cao-seo-${hostOf(url)}`;

	const buildExcel = () => [
		{name: 'Tổng quan', aoa: [
			['Chỉ số', 'Giá trị'], ...overviewRows,
			[], ['Nhóm tiêu chí', 'Điểm'], ...groupRows,
		]},
		{name: 'Tiêu chí', aoa: [criteriaHead, ...criteriaRows]},
	];

	const buildPdf = () => ({
		title: 'Báo cáo chấm điểm SEO',
		subtitle: url,
		sections: [
			{heading: 'Tổng quan', columns: ['Chỉ số', 'Giá trị'], rows: overviewRows},
			{heading: 'Điểm theo nhóm', columns: ['Nhóm tiêu chí', 'Điểm'], rows: groupRows},
			{heading: 'Chi tiết tiêu chí', columns: criteriaHead, rows: criteriaRows},
		],
	});

	return {filename, buildExcel, buildPdf};
}

/**
 * Bảng kết quả chấm điểm đầy đủ. Khi có `reportId` và `showPreview`, bố cục 2 cột:
 * trái = điểm + tiêu chí, phải = khung xem giao diện website (sticky). Click heading
 * ở cột trái → cuộn/tô sáng trên preview cột phải.
 */
function SeoResult({result, showPreview = true}) {

	const previewRef = useRef(null);
	const [expanded, setExpanded] = useState(false);

	if (!result) return null;

	const reportId = result.id;
	const previewToken = result.preview_token;
	const url = result.meta?.url;
	const withPreview = showPreview && !!reportId && !!previewToken;

	const handleHighlight = (payload) => previewRef.current?.highlight(payload);

	const {filename, buildExcel, buildPdf} = buildSeoExport(result);

	// Danh sách dùng cho panel điều hướng khi mở rộng preview.
	const findDetails = (key) => result.items?.find((i) => i.key === key)?.details?.items || [];
	const navHeadings = findDetails('heading_order');
	const navImages = findDetails('image_alt');

	return (
		<div className={cn('result', {'has-preview': withPreview})}>
			<div className={cn('result-main')}>
				<div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: 12}}>
					<ExportButtons filename={filename} buildExcel={buildExcel} buildPdf={buildPdf} />
				</div>
				<div className={cn('result-top')}>
					<ScoreOverview result={result} />
					<CrawlSummary meta={result.meta} />
				</div>
				{result.meta?.social && (
					<div className={cn('result-section')}>
						<h3 className={cn('section-title')}>Xem trước hiển thị</h3>
						<SerpPreview social={result.meta.social} />
					</div>
				)}
				<CriteriaGroup
					groups={result.groups || []}
					onHighlight={withPreview ? handleHighlight : undefined}
					hideNav={expanded}
				/>
			</div>

			{withPreview && (
				<div className={cn('result-side')}>
					<WebsitePreview
						ref={previewRef}
						reportId={reportId}
						previewToken={previewToken}
						url={url}
						headings={navHeadings}
						images={navImages}
						expanded={expanded}
						onToggleExpand={() => setExpanded((e) => !e)}
					/>
				</div>
			)}
		</div>
	);
}

export default SeoResult;
